// frontend/src/pages/admin/ManageUsers.js
import React, { useState, useEffect } from "react";
import { Search, Edit2, Trash2, UserPlus, X} from "lucide-react";
import api from "../../../services/api";
import Navbar from "../../../components/Navbar";
import "./ManageUsers.css";

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pendingRoleChange, setPendingRoleChange] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    role: "project_associate",
  });

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch users");
      setLoading(false);
    }
  };

  const handleRoleChange = (userId, newRole) => {
    const user = users.find((u) => u.id === userId);
    setPendingRoleChange({ userId, user, oldRole: user.role, newRole });
    setShowModal(true);
  };

  const confirmRoleChange = async () => {
    const { userId, newRole } = pendingRoleChange;
    try {
      await api.patch(`/users/${userId}/role`, { role: newRole });
      setUsers(
        users.map((user) =>
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
      setEditingUser(null);
    } catch (err) {
      setError("Failed to update user role");
    }
    setShowModal(false);
    setPendingRoleChange(null);
  };

  const handleCreateUser = async () => {
    try {
      const response = await api.post("/users", newUser);
      setUsers([...users, response.data]);
      setShowCreateModal(false);
      setNewUser({ name: "", email: "", password:"", role: "project_associate" });
    } catch (err) {
      setError("Failed to create user");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await api.delete(`/users/${userId}`);
        setUsers(users.filter((user) => user.id !== userId));
      } catch (err) {
        setError("Failed to delete user");
      }
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="manage-users-container">
      <Navbar role="admin" />
      <div className="content">
        <div className="header">
          <h1>Manage Team Members</h1>
          <div className="header-right">
            <button
              className="secondary-button"
              onClick={() => setShowCreateModal(true)}
            >
              <UserPlus size={20} />
              Create User
            </button>
            <div className="search-bar">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Modal for New Users */}
        {showCreateModal && (
          <div className="modal-overlay">
            <div className="document-modal">
              <div className="modal-header">
                <h2>Create New User</h2>
                <button
                  className="close-modal"
                  onClick={() => setShowCreateModal(false)}
                >
                  <X size={20} />
                </button>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateUser();
                }}
              >
                <div className="form-group modal-form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, name: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group modal-form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group modal-form-group">
                  <label>Password:</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group modal-form-group">
                  <label>Role:</label>
                  <select
                    value={newUser.role}
                    onChange={(e) =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                  >
                    <option value="project_associate">Project Associate</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="submit-button">
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    {editingUser === user.id ? (
                      <select
                        value={user.role}
                        onChange={(e) =>
                          handleRoleChange(user.id, e.target.value)
                        }
                        onBlur={() => setEditingUser(null)}
                        className="role-select"
                        autoFocus
                      >
                        <option value="admin">Admin</option>
                        <option value="project_associate">
                          Project Associate
                        </option>
                      </select>
                    ) : (
                      <span className={`role-badge ${user.role}`}>
                        {user.role}
                      </span>
                    )}
                  </td>
                  <td className="actions">
                    <button
                      className="edit-btn"
                      onClick={() => setEditingUser(user.id)}
                      title="Edit Role"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Delete User"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {showModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Confirm Role Change</h2>
              <p>
                Are you sure you want to change {pendingRoleChange.user.name}'s
                role from{" "}
                <span className="highlight">{pendingRoleChange.oldRole}</span>{" "}
                to{" "}
                <span className="highlight">{pendingRoleChange.newRole}</span>?
              </p>
              <div className="modal-actions">
                <button
                  className="cancel-button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingUser(null);
                    setPendingRoleChange(null);
                  }}
                >
                  Cancel
                </button>
                <button className="submit-button" onClick={confirmRoleChange}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageUsers;
